import { useState, useEffect } from "react";
import { useAuth } from "@/App";
import { useParams, useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  ArrowLeft,
  Send,
  Loader2,
  Paperclip,
  X,
  FileIcon
} from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FormSubmit = () => {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [template, setTemplate] = useState(null);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [severity, setSeverity] = useState('');
  const [location, setLocation] = useState('');
  const [notes, setNotes] = useState('');
  const [attachments, setAttachments] = useState([]);

  useEffect(() => {
    fetchTemplate();
  }, [templateId]);

  const fetchTemplate = async () => {
    try {
      const response = await axios.get(`${API}/forms/templates/${templateId}`, { withCredentials: true });
      setTemplate(response.data);
      
      // Initialize form data with empty values
      const initialData = {};
      response.data.fields?.forEach(field => {
        if (field.type === 'checkbox') {
          initialData[field.id] = false;
        } else if (field.type === 'multiselect') {
          initialData[field.id] = [];
        } else {
          initialData[field.id] = '';
        }
      });
      setFormData(initialData);
    } catch (error) {
      console.error("Error fetching template:", error);
      toast.error("Failed to load form template");
      navigate('/forms');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (fieldId, value) => {
    setFormData(prev => ({ ...prev, [fieldId]: value }));
  };

  const handleMultiSelectChange = (fieldId, option, checked) => {
    setFormData(prev => {
      const current = prev[fieldId] || [];
      if (checked) {
        return { ...prev, [fieldId]: [...current, option] };
      } else {
        return { ...prev, [fieldId]: current.filter(o => o !== option) };
      }
    });
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(f => f.size <= 10 * 1024 * 1024); // 10MB limit
    
    if (validFiles.length < files.length) {
      toast.error("Some files were skipped (max 10MB per file)");
    }
    
    setAttachments(prev => [...prev, ...validFiles]);
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    const missingFields = template.fields
      .filter(f => f.required && !formData[f.id] && formData[f.id] !== false)
      .map(f => f.label);
    
    if (missingFields.length > 0) {
      toast.error(`Please fill in: ${missingFields.join(', ')}`);
      return;
    }

    setSubmitting(true);
    try {
      // Submit form
      const response = await axios.post(`${API}/forms/submit`, {
        template_id: templateId,
        data: formData,
        location: location || formData.location || null,
        severity: severity || null,
        notes: notes || null
      }, { withCredentials: true });

      const submissionId = response.data.submission_id;

      // Upload attachments if any
      for (const file of attachments) {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        
        try {
          await axios.post(
            `${API}/forms/submissions/${submissionId}/attachments`,
            formDataUpload,
            { 
              withCredentials: true,
              headers: { 'Content-Type': 'multipart/form-data' }
            }
          );
        } catch (uploadError) {
          console.error("Attachment upload error:", uploadError);
        }
      }
      
      toast.success("Form submitted successfully!");
      navigate('/forms');
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error(error.response?.data?.detail || "Failed to submit form");
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (field) => {
    switch (field.type) {
      case 'text':
        return (
          <Input
            id={field.id}
            value={formData[field.id] || ''}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            placeholder={field.placeholder || ''}
            required={field.required}
            data-testid={`field-${field.id}`}
          />
        );
      
      case 'textarea':
        return (
          <Textarea
            id={field.id}
            value={formData[field.id] || ''}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            placeholder={field.placeholder || ''}
            required={field.required}
            className="min-h-[100px]"
            data-testid={`field-${field.id}`}
          />
        );
      
      case 'date':
        return (
          <Input
            id={field.id}
            type="date"
            value={formData[field.id] || ''}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            required={field.required}
            data-testid={`field-${field.id}`}
          />
        );
      
      case 'time':
        return (
          <Input
            id={field.id}
            type="time"
            value={formData[field.id] || ''}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            required={field.required}
            data-testid={`field-${field.id}`}
          />
        );
      
      case 'select':
        return (
          <Select
            value={formData[field.id] || ''}
            onValueChange={(value) => handleFieldChange(field.id, value)}
          >
            <SelectTrigger data-testid={`field-${field.id}`}>
              <SelectValue placeholder="Select an option..." />
            </SelectTrigger>
            <SelectContent>
              {field.options?.map((option, i) => (
                <SelectItem key={i} value={option}>{option}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      
      case 'checkbox':
        return (
          <div className="flex items-center gap-2">
            <Checkbox
              id={field.id}
              checked={formData[field.id] || false}
              onCheckedChange={(checked) => handleFieldChange(field.id, checked)}
              data-testid={`field-${field.id}`}
            />
            <Label htmlFor={field.id} className="text-sm font-normal cursor-pointer">
              Yes
            </Label>
          </div>
        );
      
      case 'multiselect':
        return (
          <div className="space-y-2">
            {field.options?.map((option, i) => (
              <div key={i} className="flex items-center gap-2">
                <Checkbox
                  id={`${field.id}-${i}`}
                  checked={(formData[field.id] || []).includes(option)}
                  onCheckedChange={(checked) => handleMultiSelectChange(field.id, option, checked)}
                  data-testid={`field-${field.id}-${i}`}
                />
                <Label htmlFor={`${field.id}-${i}`} className="text-sm font-normal cursor-pointer">
                  {option}
                </Label>
              </div>
            ))}
          </div>
        );
      
      default:
        return (
          <Input
            id={field.id}
            value={formData[field.id] || ''}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            required={field.required}
            data-testid={`field-${field.id}`}
          />
        );
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="w-12 h-12 border-4 border-accent border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!template) {
    return null;
  }

  const isIncidentForm = template.form_type === 'incident';

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8 max-w-4xl mx-auto" data-testid="form-submit-page">
        {/* Header */}
        <div className="mb-8">
          <Link to="/forms" className="inline-flex items-center text-muted-foreground hover:text-primary mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Forms
          </Link>
          <h1 className="font-heading text-2xl lg:text-3xl font-bold text-primary">
            {template.title}
          </h1>
          {template.description && (
            <p className="text-muted-foreground mt-1">{template.description}</p>
          )}
        </div>

        <form onSubmit={handleSubmit}>
          <Card>
            <CardContent className="p-6 space-y-6">
              {/* Severity selector for incident forms */}
              {isIncidentForm && (
                <div>
                  <Label htmlFor="severity" className="text-sm font-medium">
                    Severity Level <span className="text-destructive">*</span>
                  </Label>
                  <Select value={severity} onValueChange={setSeverity}>
                    <SelectTrigger data-testid="severity-select" className="mt-1.5">
                      <SelectValue placeholder="Select severity..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="critical">Critical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Dynamic form fields */}
              {template.fields?.map((field, index) => (
                <div key={field.id || index}>
                  <Label htmlFor={field.id} className="text-sm font-medium">
                    {field.label}
                    {field.required && <span className="text-destructive ml-1">*</span>}
                  </Label>
                  <div className="mt-1.5">
                    {renderField(field)}
                  </div>
                </div>
              ))}

              {/* Additional notes */}
              <div>
                <Label htmlFor="notes" className="text-sm font-medium">
                  Additional Notes
                </Label>
                <Textarea
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Any additional information..."
                  className="mt-1.5"
                  data-testid="additional-notes"
                />
              </div>

              {/* File Attachments */}
              <div>
                <Label className="text-sm font-medium">Attachments</Label>
                <div className="mt-1.5">
                  <div className="border-2 border-dashed border-slate-200 rounded-lg p-4 text-center hover:border-slate-300 transition-colors">
                    <input
                      type="file"
                      multiple
                      onChange={handleFileChange}
                      className="hidden"
                      id="file-upload"
                      accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                      data-testid="file-upload-input"
                    />
                    <label htmlFor="file-upload" className="cursor-pointer">
                      <Paperclip className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                      <p className="text-sm text-muted-foreground">
                        Click to upload files (max 10MB each)
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Images, PDFs, Word, Excel supported
                      </p>
                    </label>
                  </div>
                  
                  {/* Attachment list */}
                  {attachments.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {attachments.map((file, index) => (
                        <div key={index} className="flex items-center justify-between p-2 bg-secondary rounded-lg">
                          <div className="flex items-center gap-2">
                            <FileIcon className="w-4 h-4 text-muted-foreground" />
                            <span className="text-sm truncate max-w-[200px]">{file.name}</span>
                            <span className="text-xs text-muted-foreground">
                              ({(file.size / 1024).toFixed(1)} KB)
                            </span>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => removeAttachment(index)}
                            className="h-8 w-8"
                            data-testid={`remove-attachment-${index}`}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Submit button */}
              <div className="flex justify-end gap-4 pt-4 border-t">
                <Link to="/forms">
                  <Button type="button" variant="outline" data-testid="cancel-btn">
                    Cancel
                  </Button>
                </Link>
                <Button type="submit" disabled={submitting} data-testid="submit-form-btn">
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Submit Form
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </form>
      </div>
    </DashboardLayout>
  );
};

export default FormSubmit;
